<!-- eslint-disable regexp/no-unused-capturing-group -->
<script setup>
import { useModal } from 'vue-final-modal';
// --------------------------------- Imports -------------------------------- //
import { useRoute } from 'vue-router';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import useEmitter from '~/common/composables/useEmitter';
import { getFileExtension } from '~/common/utils/common.utils.js';
import TransmittalVersionRenameForm from '~/dms/components/transmittals/transmittal-sidebar/transmittal-version-rename-form.vue';
import { useTransmittalsStore } from '~/dms/store/transmittals.store.js';
import { downloadAnnotatedPDF } from '~/plans/utils/helper';

// ---------------------------------- Props --------------------------------- //

const props = defineProps({
  // eslint-disable-next-line vue/prop-name-casing
  document_details: {
    type: Object,
  },
  revisionsFeedback: {
    type: Object,
  },
  getReviewTitle: {
    type: Function,
  },
  payload: {
    type: Object,
    default: () => ({}),
  },
});

// ---------------------------------- Emits --------------------------------- //

const emit = defineEmits(['close']);

// ---------------------------- Injects/Provides ---------------------------- //

// ----------------------- Variables - Pinia - consts ----------------------- //
const {
  $date,
  $services,
  $t,
  $toast,
  common_store,
} = useCommonImports();
const transmittals_store = useTransmittalsStore();

// --------------------- Variables - Pinia - storeToRefs -------------------- //

// ------------------- Variables - Local - consts and lets ------------------ //

// ------------------------ Variables - Local - refs ------------------------ //

const state = reactive({
  show_viewer: false,
  is_fetching_annotations: false,
});
const route = useRoute();
const emitter = useEmitter();

// ---------------------- Variables - Local - reactives --------------------- //

// --------------------------- Computed properties -------------------------- //

// -------------------------------- Functions ------------------------------- //
async function downloadFile(version) {
  if (version.file_type === 'application/pdf') {
    state.is_fetching_annotations = true;
    const annotation_query = {
      element: props.document_details.uid,
      version: version.uid,
    };
    const annotation_response = await transmittals_store.fetch_annotations(annotation_query);
    if (annotation_response.data.annotations.length) {
      await downloadAnnotatedPDF(version.presigned_url, annotation_response.data.annotations, version.file_name.split('.')[0]);
    }
    else {
      window.open(version.presigned_url, '_blank');
    }
    state.is_fetching_annotations = false;
  }
  else if (version.presigned_url) {
    window.open(version.presigned_url, '_blank');
  }
}

async function downloadNoteAnnotations(version) {
  state.is_fetching_annotations = true;
  const annotation_query = {
    element: props.document_details.uid,
    version: version.uid,
  };

  const annotation_response = await transmittals_store.fetch_annotations(annotation_query);

  const annotation_payload = annotation_response.data.annotations.map(annotation => (
    {
      user: common_store.get_user_or_team_name(annotation.owner),
      action: 'added a note',
      timestamp: $date(annotation.created_at, 'DATETIME_MED'),
      has_message: (/(?<=<contents>)(.*)(?=<\/contents>)/s.exec(annotation.annotation_data)?.[0] || '')?.length > 0,
      message: /(?<=<contents>)(.*)(?=<\/contents>)/s.exec(annotation.annotation_data)?.[0] || '',
    }
  )).filter(annotation => annotation.has_message);

  if (annotation_response.status === 200) {
    const response = await $services.dms_transmittals.export_pdf({
      body: {
        ...(props.payload || {}),
        annotations: annotation_payload,
      },
    });
    const response_data = response.data;
    state.is_fetching_annotations = false;
    if (response.status === 200) {
      const { saveAs } = await import('file-saver');
      saveAs(response_data, 'Report.pdf');
      $toast({
        title: $t('Annotations downloaded'),
        type: 'success',
        position: 'bottom-right',
      });
    }
    else {
      $toast({
        title: 'Something went wrong',
        text: 'Please try again',
        type: 'error',
      });
    }
  }
}

async function getFileUrl(payload) {
  const response = await document_store.get_download_url({
    body: {
      files: [payload],
    },
  });
  const url = response.data?.urls[0]?.url;

  return url;
}

const rename_form = useModal({
  component: TransmittalVersionRenameForm,
  attrs: {
    onClose() {
      rename_form.close();
    },
  },
});

async function renameVersion(version) {
  rename_form.patchOptions({
    attrs: {
      item: version,
      isVersion: true,
      save: async (file_name) => {
        await transmittals_store.rename_transmittal_version({
          transmittal_uid: route.params.transmittal_uid,
          transmittals: {
            documents: [{
              uid: props.document_details.uid,
              versions: [{
                uid: version.uid,
                name: file_name,
              }],
            }],
          },
        });
        const versions = props.document_details?.versions;
        const current_version = versions.find(v => v.uid === version.uid);
        current_version.name = file_name;
        rename_form.close();
      },
    },
  });
  rename_form.open();
}

async function compareHandler(version) {
  const url = await getFileUrl({
    uid: state.document_details.uid,
    version_uid: version.uid,
  });
  url && emitter.emit('document_compare', url);
  emit('close');
}

function getVersionMenu(version) {
  const menu = [
    {
      label: $t('Download'),
      uid: 'download',
      on_click: async () => {
        await downloadFile(version);
      },
    },
    {
      label: $t('Download annotations history'),
      uid: 'download_note_annotations',
      on_click: async () => {
        await downloadNoteAnnotations(version);
      },
    },
    // Show download without annotations only for PDF files as other types don't have annotations
    ...(version.file_type === 'application/pdf'
      ? [
          {
            label: $t('Download without annotations'),
            uid: 'download_without_annotations',
            on_click: () => {
              window.open(version.presigned_url, '_blank');
            },
          },
        ]
      : []),
    {
      label: $t('Rename'),
      uid: 'rename',
      on_click: async () => {
        await renameVersion(version);
      },
    },
    {
      label: $t('View'),
      uid: 'view',
      on_click: () => {
        state.show_viewer = version.uid;
      },
    },
  ];

  // Show compare option only for .pdf files in current version and selected version
  if (route.name === 'document-markup' && getFileExtension(version.name)?.toLowerCase() === 'pdf' && getFileExtension(props.document_details.name)?.toLowerCase() === 'pdf') {
    menu.push({
      label: $t('Compare'),
      uid: 'compare',
      on_click: () => {
        compareHandler(version);
      },
    });
  }

  return menu;
}
// -------------------------------- Watchers -------------------------------- //

// ----------------------------- Lifecycle Hooks ---------------------------- //

function getFeedbackForVersion(version) {
  const feedback = props.revisionsFeedback[version.uid];
  return feedback;
}
</script>

<template>
  <div>
    <div v-if="document_details?.versions?.length">
      <div v-for="(version, index) in document_details.versions" :key="index" class="px-4 py-3">
        <DocumentViewerWrapper
          v-slot="{ is_attachment_supported }"
          :document="version"
          :download_with_annotation="true"
          :viewer-options="
            {
              can_annotate: false,
              element_uid: document_details.uid,
              version: version.uid,
              no_tools: true,
              module: 'dms',
            }"
          :show_viewer="state.show_viewer === version.uid"
          @close-attachment="state.show_viewer = null"
        >
          <div class="flex items-center justify-between text-sm w-full">
            <div class="flex items-center">
              <div
                v-if="version?.thumbnail_xsmall"
                class="flex-none relative cursor-pointer group mr-3"
                @click="state.show_viewer = version.uid"
              >
                <img
                  alt="thumbnail"
                  :src="version?.thumbnail_xsmall"
                  class="w-10 h-10 rounded-lg object-cover"
                >
                <div v-if="is_attachment_supported" class="absolute top-0  place-items-center hidden group-hover:grid">
                  <div class="h-10 w-10 bg-black rounded-lg bg-opacity-20" />
                  <div class="text-white absolute">
                    <IconHawkEye />
                  </div>
                </div>
              </div>
              <div>
                <p class="text-gray-900 font-medium">
                  {{ version.name }}
                  <hawk-badge v-if="version.revision_number" class="ml-2">
                    {{ version.revision_number }}
                  </hawk-badge>
                  <hawk-badge v-if="version.date_on_document" class="ml-2">
                    {{ `${$date(version.date_on_document, 'L_DATETIME_MED')}` }}
                  </hawk-badge>
                </p>

                <div class="flex items-center mt-1">
                  <span v-if="version?.created_by?.email" class="text-xs text-gray-600 mr-2"> {{ version?.created_by?.email }} </span>
                  <HawkMembers v-else :members="version?.created_by?.uid || document_details?.owner" type="badge" />
                  <span class="text-xs text-gray-600">
                    {{ `${$date(version.created_at, 'L_DATETIME_MED')}, ${version?.file_size ? $prettysize(version.file_size) : ''}` }}
                  </span>
                </div>
              </div>
            </div>
            <HawkMenu
              :items="getVersionMenu({ ...version, is_attachment_supported })"
              @click.stop
            >
              <template #trigger>
                <IconHawkDotsVertical class="text-gray-600" />
              </template>
            </HawkMenu>
          </div>
        </DocumentViewerWrapper>
        <div
          v-if="getFeedbackForVersion(version)?.length > 0"
          class="mt-4"
        >
          <hawk-accordion
            :items="[
              { uid: 'feedback', title: $t('Feedback') },
            ]"
            type="plain"
          >
            <template #header="{ item, is_open }">
              <div class="font-semibold gap-2 mb-2 text-gray-900 text-sm flex">
                <IconHawkChevronDown v-if="is_open" />
                <IconHawkChevronRight v-else />
                {{ item.title }}
              </div>
            </template>
            <template #content>
              <div class="relative">
                <div class="absolute left-5 h-full border-l" />
                <div v-for="feedback in getFeedbackForVersion(version)" :key="feedback" class="bg-white relative z-1">
                  <TransmittalDetailMemberFeedback
                    v-if="feedback?.feedback?.length"
                    :title="getReviewTitle(feedback.type) || feedback.name"
                    :feedback="feedback"
                    :reviews="feedback?.feedback"
                    :document="document_details"
                  />
                </div>
              </div>
            </template>
          </hawk-accordion>
        </div>
      </div>
    </div>
    <div v-if="state.is_fetching_annotations" class="fixed z-50 right-6 bottom-10">
      <div
        class="bg-gray-800 px-4 text-white shadow-lg rounded-lg w-[400px] flex items-center justify-between text-sm"
        :class="state.is_fetching_annotations ? 'py-2.5' : 'py-4'"
      >
        <div class="flex gap-4 items-center">
          <div v-if="state.is_fetching_annotations" class="h-6 w-6 grid place-items-center ">
            <HawkLoader container_class="m-0" :height="5" :width="5" />
          </div>
          <HawkIconExportTick v-else />
          <div>
            <div class="font-semibold">
              <div>
                {{ $t('Fetching annotations') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
